import { Component, computed, inject, input } from '@angular/core';
import { RouterModule } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import type { SortSelection } from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import { PRICING_TOOLTIP_OCCUPANCY } from '@valk-nx/core/lib/core';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { FilterableListService } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';
import type { ContentData } from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import type {
	FilterableHotel,
	TransformedFilterableHotel,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

import {
	sortByOption,
	toBaseUrl,
	toPageUrl,
} from '../../filterable-list.helpers';

@Component({
	host: { class: 'contents' },
	selector: 'sb-filterable-list-vdc-hotels',
	templateUrl: './vdc-hotels.html',
	imports: [
		AlertComponent,
		PersuadeCardComponent,
		RouterModule,
		TranslatePipe,
	],
})
export class VdcHotels {
	private readonly filterableListService = inject(FilterableListService);
	private readonly bookingtoolUrl = inject(BOOKINGTOOL);

	sortOption = input.required<SortSelection>();
	baseUrl = input.required({ transform: toBaseUrl });
	showPrices = input<boolean>(true);
	business = input.required<boolean>();

	readonly pricingTooltipOccupancy = PRICING_TOOLTIP_OCCUPANCY;

	hotels = computed<TransformedFilterableHotel[]>(
		() =>
			this.filterableListService.vdcHotels
				.value()
				?.hotels.map((hotel) => ({
					...hotel,
					content: this.transformContent(hotel.content),
					toUrl: this.buildToUrl(hotel),
					// Business cards link through the CTA only — no card
					// (read-more) link at all.
					cardUrl: this.business()
						? undefined
						: toPageUrl(this.baseUrl(), hotel.slug),
				})) ?? [],
	);

	sortedHotels = computed<TransformedFilterableHotel[]>(() =>
		sortByOption(this.hotels(), this.sortOption()),
	);

	private readonly buildToUrl = (
		hotel: FilterableHotel,
	): string | undefined => {
		if (this.business()) {
			return toPageUrl(this.baseUrl(), hotel.slug);
		}

		if (hotel.availability.isBookable) {
			return `${this.bookingtoolUrl}?hotelSlug=${hotel.slug}`;
		}

		return undefined;
	};

	private readonly transformContent = (content: ContentData): string => {
		if (content?.type === 'array') {
			return content.value.map((item) => `- ${item}`).join('\n');
		}
		return content.value as string;
	};
}
